import { UploadOutlined } from "@ant-design/icons";
import { UploadFile } from "antd";
import { Content } from "antd/lib/layout/layout";
import {
  useGetImportForumSeminarSessionsTemplate,
  useImportForumSeminarSessionsMutation,
  useValidateImportForumSeminarSessionsMutation,
} from "api/mutations/fileImport";
import DateRenderer from "components/DateRenderer";
import { Button, Drawer, Form, Row, Space, Upload } from "components/styleguide";
import { Em2AxiosError } from "error-handling/errorUtil";
import {
  Em2ExceptionTypes,
  ForumSeminarSessionsImportItem,
  ForumSeminarSessionsImportResponse,
  InvalidModelStateExceptionResponseMeta,
  ObjectNotFoundExceptionResponseMeta,
} from "generated/api";
import {
  downloadBinaryFile,
  DownloadFileData,
  extractErrorFile,
  extractTemplateFile,
} from "pages/ConferenceTimings/helpers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { handleFileFakeRequest } from "utils/documentUtils";
import { v4 as uuidv4 } from "uuid";

import ConferenceTimingsTable from "./ConferenceTimingsTable/ConferenceTimingsTable";

interface ConferenceTimingsDrawerProps {
  forumId: number;
  visible: boolean;
  onClose: () => void;
  onUpload?: () => void;
  onApprove?: () => void;
}

const ConferenceTimingsDrawer: React.FC<ConferenceTimingsDrawerProps> = ({
  forumId,
  visible,
  onClose,
  onUpload,
  onApprove,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null);
  const createMutation = useImportForumSeminarSessionsMutation();
  const validateMutation = useValidateImportForumSeminarSessionsMutation();

  const [isValidationPassed, setIsValidationPassed] = useState(false);
  const [forumRelatedRows, setForumRelatedRows] = useState<ForumSeminarSessionsImportItem[]>([]);
  const [successCount, setSuccessCount] = useState<number | undefined>(undefined);
  const [uploadedBy, setUploadedBy] = useState<string | undefined>(undefined);
  const [uploadedAt, setUploadedAt] = useState<string | undefined>(undefined);

  const [errorFile, setErrorFile] = useState<DownloadFileData | null>(null);

  const getTemplateQuery = useGetImportForumSeminarSessionsTemplate(
    {},
    {
      enabled: false,
      refetchOnWindowFocus: false,
      onSuccess: ({ data }) => {
        toast.success(t("Template downloaded successfully"), { autoClose: 30000 });
        if (data && data.templateFile && data.templateContentType && data.templateName) {
          const templateData = extractTemplateFile(data);
          if (templateData) {
            downloadBinaryFile(templateData);
          }
        } else {
          toast.error(t("Failed to download template"), { autoClose: 30000 });
        }
      },
      onError: () => {
        toast.error(t("Something went wrong while downloading the template"), { autoClose: 30000 });
      },
    },
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRequestError = (error: any) => {
    const err = new Em2AxiosError(error);

    if (err.isOfType(Em2ExceptionTypes.ObjectNotFound)) {
      const meta = err.getMeta<ObjectNotFoundExceptionResponseMeta>();
      toast.error(`${meta.ObjectType} not found: ${Object.values(meta.ObjectFields ?? []).join(", ")}`, {
        autoClose: 30000,
      });
    } else if (err.isOfType(Em2ExceptionTypes.InvalidModelState)) {
      const meta = err.getMeta<InvalidModelStateExceptionResponseMeta>();
      toast.error(t("Invalid data: {{errors}}", { errors: Object.values(meta.ModelFields ?? []).join(", ") }), {
        autoClose: 30000,
      });
    } else if (error.response.data.Type === "DuplicateNameException") {
      toast.error(
        t("Failed to write errors in the file. Please verify if source file is a valid file (without 'Error' column)."),
        { autoClose: 30000 },
      );
    } else {
      console.error(error);
      toast.error(t("Something went wrong."), { autoClose: 30000 });
    }
  };

  const handleResponse = (data: ForumSeminarSessionsImportResponse, isConferenceTimingsApprove: boolean) => {
    const success = data.isSuccess ?? false;

    const successRowsCount = data.successItemsCount ?? 0;
    const failedRowsCount = data.failedItemsCount ?? 0;

    if (!success) {
      toast.error(
        t("Validation failed for {{count}} current Forum related items. Please download the error file below.", {
          count: failedRowsCount,
        }),
        { autoClose: 30000 },
      );

      if (data.errorFile) {
        setErrorFile(extractErrorFile(data.errorFile));
      } else {
        toast.error(
          t(
            "Failed to write errors in the file. Please verify if source file is a valid file (without 'Error' column).",
          ),
          { autoClose: 30000 },
        );
      }
      setIsValidationPassed(false);
    } else {
      toast.success(
        isConferenceTimingsApprove
          ? t("Successfully created {{count}} Forum Seminar Sessions", {
              count: successRowsCount,
            })
          : t("Validation succeeded for all {{count}} current Forum related items", {
              count: successRowsCount,
            }),
        { autoClose: 30000 },
      );
      setIsValidationPassed(isConferenceTimingsApprove ? false : true);
    }

    setForumRelatedRows(data.forumRelatedItems || []);
    setSuccessCount(successRowsCount);
    setUploadedAt(data.uploadedAt);
    setUploadedBy(data.uploadedBy);

    return success;
  };

  //Validation

  const handleValidateRequestSuccess = ({ data }: { data: ForumSeminarSessionsImportResponse }) => {
    handleResponse(data, false);
    onUpload?.();
  };

  const handleUpload = () => {
    if (!selectedFile?.originFileObj) {
      toast.info(t("Select file first"), { autoClose: 10000 });
      return;
    }

    setIsValidationPassed(false);
    setErrorFile(null);

    validateMutation.mutate(
      {
        forumId: forumId,
        formFile: selectedFile.originFileObj,
        importMode: "Upsert", //not actually used
        importKey: uuidv4(), //not actually used
      },
      {
        onSuccess: handleValidateRequestSuccess,
        onError: handleRequestError,
      },
    );
  };

  //Approve Create
  const handleCreateRequestSuccess = ({ data }: { data: ForumSeminarSessionsImportResponse }) => {
    handleResponse(data, true);
    onApprove?.();
  };

  const handleApprove = () => {
    if (!isValidationPassed) {
      toast.info(t("Upload file first"), { autoClose: 10000 });
      return;
    }

    if (forumRelatedRows.length === 0) {
      toast.info(t("No rows to Approve"), { autoClose: 10000 });
      return;
    }

    setIsValidationPassed(false);
    setErrorFile(null);

    createMutation.mutate(
      {
        forumId: forumId,
        importKey: uuidv4(),
        importMode: "Upsert",
        formFile: selectedFile?.originFileObj,
      },
      {
        onSuccess: handleCreateRequestSuccess,
        onError: handleRequestError,
      },
    );
  };

  const handleDownloadTemplate = async () => {
    await getTemplateQuery.refetch();
  };

  //Helpers
  const downloadErrorFile = () => {
    if (!errorFile) return;

    downloadBinaryFile(errorFile);
  };

  const clearAllData = () => {
    setForumRelatedRows([]);
    setIsValidationPassed(false);
    setErrorFile(null);
    setSuccessCount(undefined);
    setUploadedAt(undefined);
    setUploadedBy(undefined);
  };

  const handleClear = () => {
    clearAllData();
    form.resetFields();
  };

  const handleClose = () => {
    clearAllData();
    onClose();
  };

  const isLoading = validateMutation.isLoading || createMutation.isLoading;

  return (
    <Drawer
      title={t("Upload Timings")}
      open={visible}
      onClose={handleClose}
      placement="right"
      size="large"
      destroyOnClose
      contentWrapperStyle={{ minWidth: "60%" }}
    >
      {/* Upload Form */}
      <Form form={form} onFinish={handleUpload}>
        <Form.Item name="formFile" rules={[{ required: true, message: t("errors.required", { prop: "$t(File)" }) }]}>
          <Upload.Dragger
            maxCount={1}
            accept=".xlsx,.xls,.xlsm"
            beforeUpload={clearAllData}
            onRemove={clearAllData}
            customRequest={handleFileFakeRequest}
            onChange={(file) => setSelectedFile(file.file)}
          >
            <h1>
              <UploadOutlined />
            </h1>
            <p>{t("Drag file here or Browse")}</p>
            <p>
              <strong>{t("Allowed formats")}:</strong> .xlsx, .xls, .xlsm
            </p>
          </Upload.Dragger>
        </Form.Item>

        {/* Uploaded At and By */}

        {uploadedAt && uploadedBy && (
          <Space direction="vertical" style={{ marginBottom: 20 }}>
            <Row>
              <strong>{t("Uploaded By")}</strong>: {uploadedBy}
            </Row>
            <Row>
              <strong>{t("Uploaded At")}</strong>: <DateRenderer date={uploadedAt} showTime />
            </Row>
          </Space>
        )}

        <Row justify="space-between">
          <Row>
            <Button onClick={handleClear} loading={isLoading}>
              {t("Clear")}
            </Button>
            <Button style={{ marginLeft: 10 }} onClick={handleDownloadTemplate} loading={getTemplateQuery.isFetching}>
              {t("Download Import Template")}
            </Button>
          </Row>
          <Row>
            {errorFile && (
              <Button onClick={downloadErrorFile} style={{ marginRight: 10, border: "1px solid orange" }}>
                {t("Download Import Errors")}
              </Button>
            )}
            <Button type="primary" htmlType="submit" disabled={isLoading} loading={isLoading}>
              {t("Upload")}
            </Button>
          </Row>
        </Row>
      </Form>

      {/* Info */}

      {successCount !== undefined && forumRelatedRows.length > 0 && (
        <>
          <Row style={{ marginTop: 20 }}>
            <span>
              <strong>{t("Rows related to current Forum")}</strong>: {forumRelatedRows.length}
            </span>
          </Row>
          {successCount !== forumRelatedRows.length ? (
            <Row style={{ marginTop: 5 }}>
              <span>
                {t("{{count}} row(s) must be corrected before upload is possible.", {
                  count: forumRelatedRows.length - successCount,
                })}
              </span>
            </Row>
          ) : (
            <Row style={{ marginTop: 5 }}>
              <span>{t("All {{count}} row(s) are valid.", { count: forumRelatedRows.length })}</span>
            </Row>
          )}
        </>
      )}

      {/* Table */}
      <Content style={{ marginTop: 20 }}>
        <ConferenceTimingsTable rows={forumRelatedRows} loading={isLoading} />
      </Content>

      {/* Approve Button */}

      {isValidationPassed && forumRelatedRows.length > 0 && (
        <Row justify="end" style={{ marginTop: 40 }}>
          <Button type="primary" onClick={handleApprove} disabled={isLoading} loading={isLoading}>
            {t("Approve")}
          </Button>
        </Row>
      )}
    </Drawer>
  );
};

export default ConferenceTimingsDrawer;
